import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { BellRing, X } from "lucide-react";

export default function PulseAlertConfigDialog({ wsId, brand, onClose, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [emailInput, setEmailInput] = useState("");

  useEffect(() => {
    if (!wsId || !brand?.id) return;
    api.get(`/workspaces/${wsId}/brands/${brand.id}/pulse-alerts`)
      .then((r) => setCfg(r.data))
      .catch((e) => toast.error(apiErrorMessage(e, "Failed to load alert config")));
  }, [wsId, brand?.id]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const addEmail = () => {
    const e = (emailInput || "").trim();
    if (!e || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      toast.error("Enter a valid email");
      return;
    }
    const list = cfg?.notify_email || [];
    if (list.includes(e)) return;
    set("notify_email", [...list, e]);
    setEmailInput("");
  };
  const removeEmail = (e) => set("notify_email", (cfg?.notify_email || []).filter((x) => x !== e));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/workspaces/${wsId}/brands/${brand.id}/pulse-alerts`, {
        enabled: !!cfg.enabled,
        threshold_low: cfg.threshold_low === "" || cfg.threshold_low === null ? null : Number(cfg.threshold_low),
        threshold_high: cfg.threshold_high === "" || cfg.threshold_high === null ? null : Number(cfg.threshold_high),
        drop_delta: cfg.drop_delta === "" || cfg.drop_delta === null ? null : Number(cfg.drop_delta),
        consecutive_weeks: Number(cfg.consecutive_weeks || 2),
        notify_email: cfg.notify_email || [],
      });
      toast.success("Alert thresholds saved");
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="pulse-alert-config-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BellRing className="w-4 h-4 text-amber-600" />
            Pulse alerts · {brand?.name || "Brand"}
          </DialogTitle>
        </DialogHeader>
        {!cfg ? (
          <div className="text-xs text-muted-foreground py-4">Loading…</div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between border border-border p-3 bg-card">
              <div>
                <div className="text-sm font-semibold">Alerts enabled</div>
                <div className="text-[10px] text-muted-foreground">Notifications fire on the next capture that meets these rules.</div>
              </div>
              <Switch checked={!!cfg.enabled} onCheckedChange={(v) => set("enabled", v)}
                        data-testid="pulse-alert-enabled" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-eyebrow mb-1 inline-block">Low threshold (0-100)</Label>
                <Input type="number" min={0} max={100} value={cfg.threshold_low ?? ""}
                         onChange={(e) => set("threshold_low", e.target.value)}
                         className="h-9 rounded-none" placeholder="35"
                         data-testid="pulse-alert-threshold-low" />
                <div className="text-[9px] text-muted-foreground mt-1">Fires when pulse ≤ this for N consecutive captures.</div>
              </div>
              <div>
                <Label className="label-eyebrow mb-1 inline-block">Consecutive captures</Label>
                <Input type="number" min={1} max={12} value={cfg.consecutive_weeks ?? 2}
                         onChange={(e) => set("consecutive_weeks", e.target.value)}
                         className="h-9 rounded-none"
                         data-testid="pulse-alert-consecutive" />
              </div>
              <div>
                <Label className="label-eyebrow mb-1 inline-block">High threshold (0-100)</Label>
                <Input type="number" min={0} max={100} value={cfg.threshold_high ?? ""}
                         onChange={(e) => set("threshold_high", e.target.value)}
                         className="h-9 rounded-none" placeholder="Optional"
                         data-testid="pulse-alert-threshold-high" />
                <div className="text-[9px] text-muted-foreground mt-1">Fires when pulse ≥ this — celebrate wins.</div>
              </div>
              <div>
                <Label className="label-eyebrow mb-1 inline-block">Sudden drop Δ</Label>
                <Input type="number" min={1} max={100} value={cfg.drop_delta ?? ""}
                         onChange={(e) => set("drop_delta", e.target.value)}
                         className="h-9 rounded-none" placeholder="Optional"
                         data-testid="pulse-alert-drop-delta" />
                <div className="text-[9px] text-muted-foreground mt-1">Fires when pulse drops by this many points in one capture.</div>
              </div>
            </div>

            <div>
              <Label className="label-eyebrow mb-1 inline-block">Email notifications</Label>
              <div className="flex gap-2">
                <Input value={emailInput}
                         onChange={(e) => setEmailInput(e.target.value)}
                         onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
                         placeholder="person@company.com"
                         className="h-9 rounded-none flex-1"
                         data-testid="pulse-alert-email-input" />
                <Button variant="outline" className="rounded-none h-9" onClick={addEmail}
                          data-testid="pulse-alert-email-add-btn">Add</Button>
              </div>
              <div className="flex flex-wrap gap-1 mt-2" data-testid="pulse-alert-email-list">
                {(cfg.notify_email || []).map((e) => (
                  <span key={e} className="inline-flex items-center gap-1 text-[10px] border border-border bg-muted/40 px-1.5 py-0.5">
                    {e}
                    <button type="button" onClick={() => removeEmail(e)} className="hover:text-rose-600">
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </span>
                ))}
                {!(cfg.notify_email || []).length && <span className="text-[10px] text-muted-foreground italic">No emails yet</span>}
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={saving || !cfg}
                    data-testid="pulse-alert-save-btn">
            {saving ? "Saving…" : "Save alerts"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
